import { useMemo } from "react";
import { Deal } from "@/types/deal";

export type YearKey = `y${number}`;

export interface KpiDataByYear {
  years: YearKey[];
  adr: Record<YearKey, number>;
  occupancy: Record<YearKey, number>;
  revpar: Record<YearKey, number>;
  toplineIndex: Record<YearKey, number>;
  inflationIndex: Record<YearKey, number>;
  revenueRamp: Record<YearKey, number>;
  costRamp: Record<YearKey, number>;
}

export function useKpiDataV2(
  deal: Deal,
  yearCount: number = 10,
): KpiDataByYear {
  return useMemo(() => {
    const baseADR = deal.kpis?.roomRevenue?.totals?.avgADR || 20;
    const baseOccupancyPct = deal.kpis?.roomRevenue?.totals?.avgOccPct || 75.38;

    const rampSettings = deal.kpis?.rampSettings;
    const revenueRamp = rampSettings?.revenueRamp || [0.7, 0.8, 0.9, 1.0];
    const costRamp = rampSettings?.costRamp || [1.15, 1.1, 1.0, 1.0];

    const toplineGrowthRate = (rampSettings?.toplineGrowthPct || 2) / 100;
    const inflationRate = (rampSettings?.inflationPct || 2) / 100;

    const years: YearKey[] = [];
    const adr: Record<YearKey, number> = {};
    const occupancy: Record<YearKey, number> = {};
    const revpar: Record<YearKey, number> = {};
    const toplineIndex: Record<YearKey, number> = {};
    const inflationIndex: Record<YearKey, number> = {};
    const revenueRampMul: Record<YearKey, number> = {};
    const costRampMul: Record<YearKey, number> = {};

    // Pre-Op (y0)
    years.push("y0");
    adr.y0 = 0;
    occupancy.y0 = 0;
    revpar.y0 = 0;
    toplineIndex.y0 = 1;
    inflationIndex.y0 = 1;
    revenueRampMul.y0 = 0;
    costRampMul.y0 = 1;

    for (let yearNum = 1; yearNum <= yearCount; yearNum++) {
      const yearKey: YearKey = `y${yearNum}`;
      years.push(yearKey);

      const revRampMul = yearNum <= 4 ? revenueRamp[yearNum - 1] : 1.0;
      const costRampMulVal = yearNum <= 4 ? costRamp[yearNum - 1] : 1.0;

      // FIX: Year 1 = 1.0 (no growth yet — this is the stabilized base year).
      // Without this fix, ADR/RevPAR in this table would differ from the P&L table.
      const toplineIdx = Math.pow(1 + toplineGrowthRate, yearNum);
      const inflationIdx = Math.pow(1 + inflationRate, yearNum);

      revenueRampMul[yearKey] = revRampMul;
      costRampMul[yearKey] = costRampMulVal;
      toplineIndex[yearKey] = toplineIdx;
      inflationIndex[yearKey] = inflationIdx;

      const calculatedAdr = baseADR * revRampMul * toplineIdx;
      const calculatedOcc = Math.min(baseOccupancyPct * revRampMul, 100); // cap 100%
      const calculatedRevpar = calculatedAdr * (calculatedOcc / 100);

      adr[yearKey] = calculatedAdr;
      occupancy[yearKey] = calculatedOcc;
      revpar[yearKey] = calculatedRevpar;
    }

    return {
      years,
      adr,
      occupancy,
      revpar,
      toplineIndex,
      inflationIndex,
      revenueRamp: revenueRampMul,
      costRamp: costRampMul,
    };
  }, [deal, yearCount]);
}
